import {
  AssetType,
  BatteryDataType,
  GasProduction,
  GasProductionDataType,
  GasStorage,
  GasStorageDataType,
  MotorDataType,
  OptimizationAdminPanel,
  OptimizationGroupItems,
  OptimizationGroupParameters,
  OptimizationGroupParametersType,
  OptimizationIdSelect,
  OptimizationRunner,
  PVSystem,
  PVSystemDataType,
} from '@e2m-customer-portal/domains';
import {
  MarketLocation,
  useFeatureFlags,
  useOptimizationGroupData,
  useOptimizationGroupDataUpdate,
  useSnackbar,
  useTokenInfo,
} from '@e2m-customer-portal/hooks';
import { Nullable, optimizationWhitelist, UserRole } from '@e2m-customer-portal/shared';
import { Loading, PageTitle } from '@e2m-customer-portal/ui';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
interface OptimizationGroupPageProps {
  optimizationGroupId: string;
  marketLocations: Array<MarketLocation>;
  isTestEnvironment?: boolean;
}

export function OptimizationGroupPage({
  optimizationGroupId,
  marketLocations,
  isTestEnvironment,
}: Readonly<OptimizationGroupPageProps>): ReactElement<OptimizationGroupPageProps> {
  const [t] = useTranslation();
  const { showError, showSuccess } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [id, setId] = useState<string>(optimizationGroupId);

  const { data, loadingData } = useOptimizationGroupData(id);

  const { areOptimizationParametersEditable, areOptimizationParametersEditableForEveryone } = useFeatureFlags();
  const { roles } = useTokenInfo();
  /**
   * Returns `true` if
   * * the feature flag disables editing in general
   * * any of the market locations of the group do not allow editing
   * * the user isn’t a VPP operator and the provided parameter isn’t whitelisted
   * @param parameter The parameter the whitelist needs to return `false` for.
   * @returns `true` if the parameter or the whole form itself is read-only, otherwise `false`
   */
  const readOnly = (parameter?: string, specialCondition?: boolean) => {
    if (!areOptimizationParametersEditable) return true;
    else if (roles.has(UserRole.VPPOperator)) return false;
    else if (specialCondition) return true;
    return (
      !areOptimizationParametersEditableForEveryone ||
      marketLocations.some((malo) => !malo.permissions.editForecast) ||
      (!!parameter && !optimizationWhitelist[parameter])
    );
  };

  const [update] = useOptimizationGroupDataUpdate();

  const [assetType, setAssetType] = useState<AssetType | undefined>();

  const [gasStorageData, setGasStorageData] = useState<Nullable<GasStorageDataType>>(undefined);
  const [gasProductionData, setGasProductionData] = useState<Nullable<GasProductionDataType>>(undefined);
  const [PVSystemData, setPVSystemData] = useState<Nullable<PVSystemDataType>>(undefined);
  const [PVStandAloneData, setPVStandAloneData] = useState<Nullable<PVSystemDataType[]>>(undefined);
  const [optimizationGroupParametersData, setOptimizationGroupParametersData] =
    useState<Nullable<OptimizationGroupParametersType>>(undefined);
  const [assetName, setAssetName] = useState(undefined);
  const [itemsData, setItemsData] = useState<Nullable<BatteryDataType[] | MotorDataType[]>>(undefined);

  useEffect(() => {
    if (data?.json) {
      const configuration = JSON.parse(data.json);
      setAssetType(configuration.asset_type);
      setOptimizationGroupParametersData(configuration);
      setAssetName(configuration.name);
      switch (assetType) {
        case AssetType.InnoTender:
        case AssetType.InnoTenderGE:
          setPVSystemData(configuration.pv);
          setItemsData([configuration.battery]);
          return;
        case AssetType.CoLocation:
          setPVStandAloneData(configuration.pv);
          setItemsData(configuration.batteries);
          return;
        case AssetType.Biogas:
          setGasStorageData(configuration.storage);
          setGasProductionData(configuration.gas_production);
          setItemsData(configuration.motors);
          return;
        case AssetType.PeakShavingBatteries:
        case AssetType.FleximumBatteries:
        case AssetType.AFRRBatteries:
          setItemsData(configuration.batteries);
          return;
        case AssetType.Danpower:
          setOptimizationGroupParametersData(configuration);
      }
    }
  }, [data, assetType, id]);

  const updateValues = (parameters: string) => {
    setIsSaving(true);
    update(id, parameters)
      .then((res) => {
        if (res.data) {
          showSuccess(t('COPT_EDIT_SUCCESS_MESSAGE'));
        }
      })
      .catch((error) => {
        showError(error);
      })
      .finally(() => setIsSaving(false));
  };

  if (loadingData) {
    return <Loading />;
  }

  return (
    <div className="overflow-hidden">
      <PageTitle
        title={t('COPT_GROUP_TITLE', { name: assetName || marketLocations[0].externalName || marketLocations[0].name })}
        childrenContainerStyle="w-full flex md:justify-end items-center"
      >
        <div className="flex flex-wrap gap-2">
          {isTestEnvironment && <OptimizationIdSelect setId={setId} optimizationGroupId={id} />}
          {!readOnly() && <OptimizationRunner id={id} />}
        </div>
      </PageTitle>
      {optimizationGroupParametersData?.status === 'test' && (
        <div className="p-6 m-6 bg-warning/10 border-solid border border-warning text-warning rounded-md">
          <h2 className="m-0">{t('COPT_STATUS_TEST_WARNING_TITLE')}</h2>
          <p className="m-0">{t('COPT_STATUS_TEST_WARNING')}</p>
        </div>
      )}
      {optimizationGroupParametersData && (
        <OptimizationGroupParameters
          data={optimizationGroupParametersData}
          updateValues={updateValues}
          assetType={assetType}
          isSaving={isSaving}
          readOnly={readOnly}
        />
      )}
      {gasStorageData && (
        <GasStorage data={gasStorageData} updateValues={updateValues} isSaving={isSaving} readOnly={readOnly} />
      )}
      {gasProductionData && (
        <GasProduction data={gasProductionData} updateValues={updateValues} isSaving={isSaving} readOnly={readOnly} />
      )}
      {(assetType === AssetType.InnoTender || assetType === AssetType.InnoTenderGE) && PVSystemData && (
        <PVSystem
          data={PVSystemData}
          assetType={assetType}
          updateValues={updateValues}
          isSaving={isSaving}
          readOnly={readOnly}
        />
      )}
      {itemsData && (
        <OptimizationGroupItems
          data={itemsData}
          pvData={PVStandAloneData}
          groupId={id}
          assetType={assetType}
          readOnly={readOnly}
        />
      )}
      {data?.json && !readOnly('full JSON') && <OptimizationAdminPanel json={data.json} updateValues={updateValues} />}
    </div>
  );
}
